import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertCircle, ShieldCheck, Copy, Download, ArrowLeft } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { enableMfa, confirmMfaEnrollment, requestEnrollment } from '../services/auth.service';
import type { TotpSetupResult } from '../services/auth.service';
import { extractServerErrors } from '@/lib/api/error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

type Step = 'loading' | 'scan' | 'verify' | 'codes';

const codeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});
type CodeValues = z.infer<typeof codeSchema>;

const APP_INITIALS = 'AV';

// Extract the base32 secret embedded in an otpauth:// URI for users who can't scan.
function extractTotpSecret(qrUri: string): string | null {
  try {
    const url = new URL(qrUri);
    return url.searchParams.get('secret');
  } catch {
    return null;
  }
}

export function MfaEnrollmentComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const et = searchParams.get('et') ?? undefined;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const clearMfaGracePeriod = useAuthStore((s) => s.clearMfaGracePeriod);

  const [step, setStep] = useState<Step>('loading');
  const [setup, setSetup] = useState<TotpSetupResult | null>(null);
  const [activeEnrollmentToken, setActiveEnrollmentToken] = useState<string | undefined>(et);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [initError, setInitError] = useState<string | null>(null);
  const [savedCodes, setSavedCodes] = useState(false);

  const form = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });
  const { isSubmitting } = form.formState;

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const token = et ?? (await requestEnrollment());
        const data = await enableMfa(token);
        if (!cancelled) {
          setActiveEnrollmentToken(token);
          setSetup(data);
          setStep('scan');
        }
      } catch (err) {
        if (!cancelled) {
          if (isAxiosError(err) && err.response) {
            const msgs = extractServerErrors(err);
            setInitError(msgs[0] ?? 'Unable to start MFA setup. Please sign in again.');
          } else {
            setInitError('Network error. Please check your connection.');
          }
        }
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [et]);

  if (!et && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  async function onVerifySubmit(values: CodeValues) {
    try {
      const result = await confirmMfaEnrollment(values.code, activeEnrollmentToken);
      setRecoveryCodes(result.recoveryCodes);
      setStep('codes');
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        const msgs = extractServerErrors(err);
        form.setError('code', {
          message: msgs[0] ?? 'Invalid code. Please try again.',
        });
      } else {
        toast.error('Network error. Please check your connection.');
      }
    }
  }

  function handleCopyAll() {
    navigator.clipboard
      .writeText(recoveryCodes.join('\n'))
      .then(() => toast.success('Recovery codes copied to clipboard'))
      .catch(() => toast.error('Copy failed — please select and copy manually'));
  }

  function handleDownload() {
    const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agrovet-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleFinish() {
    clearMfaGracePeriod();
    if (et) {
      navigate('/login', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }

  const manualSecret = setup ? extractTotpSecret(setup.qrUri) : null;

  return (
    <div className="flex min-h-screen items-start justify-center bg-muted/30 px-4 pb-4 pt-8 sm:pt-12">
      <Card className="w-full max-w-sm border-border/50 shadow-xl">
        <CardHeader className="space-y-3 pb-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#22c55e] shadow-sm">
              <span className="text-lg font-bold text-white">{APP_INITIALS}</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-muted-foreground" />
              <h1 className="text-lg font-bold tracking-tight">
                Set up two-factor authentication
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              {step === 'codes'
                ? 'Save your recovery codes in a safe place'
                : 'Protect your account with an authenticator app'}
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {step === 'loading' && !initError && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {initError && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{initError}</span>
              </div>
              <Link
                to="/login"
                className="flex items-center justify-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to login
              </Link>
            </div>
          )}

          {step === 'scan' && setup && (
            <div className="space-y-4">
              <ol className="space-y-2 text-sm">
                <li>
                  <span className="font-medium">1. Install</span> an authenticator app
                  (Google Authenticator, Authy, 1Password).
                </li>
                <li>
                  <span className="font-medium">2. Scan</span> the QR code below or enter the
                  key manually.
                </li>
                <li>
                  <span className="font-medium">3. Tap <em>Next</em></span> to confirm with a
                  6-digit code.
                </li>
              </ol>

              <div className="flex flex-col items-center gap-3 rounded-md border bg-muted/40 p-4">
                <QRCodeCanvas value={setup.qrUri} size={176} level="M" />
                {manualSecret && (
                  <div className="w-full space-y-1">
                    <p className="text-xs text-muted-foreground">Manual entry key</p>
                    <code className="block break-all rounded bg-muted px-2 py-1 text-xs font-mono">
                      {manualSecret}
                    </code>
                  </div>
                )}
              </div>

              <Button className="w-full" onClick={() => setStep('verify')}>
                Next
              </Button>
            </div>
          )}

          {step === 'verify' && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onVerifySubmit)} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code shown in your authenticator app to confirm setup.
                </p>
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification code</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          autoFocus
                          maxLength={6}
                          placeholder="123456"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      form.reset();
                      setStep('scan');
                    }}
                  >
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Confirming…' : 'Confirm'}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {step === 'codes' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Store these codes somewhere safe. Each code can only be used once if you lose
                access to your authenticator app.
              </p>

              <div className="rounded-md border bg-muted/40 p-3">
                <ul className="space-y-1">
                  {recoveryCodes.map((code) => (
                    <li key={code} className="font-mono text-sm tabular-nums">
                      {code}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleCopyAll}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy all
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleDownload}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>

              <div className="flex items-start gap-3 rounded-md border px-3 py-2.5">
                <Checkbox
                  id="saved-codes"
                  checked={savedCodes}
                  onCheckedChange={(v) => setSavedCodes(v === true)}
                  className="mt-0.5"
                />
                <label htmlFor="saved-codes" className="text-sm leading-snug cursor-pointer">
                  I have saved my recovery codes in a safe place
                </label>
              </div>

              <Button className="w-full" disabled={!savedCodes} onClick={handleFinish}>
                Finish
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
